import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import React, { useRef, useState } from "react";
import "./Trend.css";
import { Construct_code_category } from "../Constants.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
);

const POINT_STYLES = [
  "circle",
  "triangle",
  "rectRounded",
  "rect",
  "rectRot",
  "cross",
  "star",
  "crossRot",
  "dash",
  "line",
];

const DASH_STYLES = [
  [],            // solid
  [6, 4],        // dashed
  [2, 2],        // dotted
  [8, 3, 2, 3],  // dash-dot
  [12, 4],       // long dash
];

function styleFor(i) {
  return {
    pointStyle: POINT_STYLES[i % POINT_STYLES.length],
    borderDash:  DASH_STYLES[i % DASH_STYLES.length],
  };
}

// const Construct_code_category = [
//   { construct: "Self-Efficacy", code: "se", category: "Student Beliefs" },
//   { construct: "Cognitive Engagement", code: "cog", category: "Learning Behaviors" },
//   { construct: "Behavioral Engagement", code: "beh", category: "Learning Behaviors" },
//   { construct: "Affective Engagement", code: "aff", category: "Learning Behaviors" },
//   { construct: "Autonomy", code: "aut", category: "Classroom Context" },
//   { construct: "Relatedness", code: "rel", category: "Classroom Context" },
//   { construct: "Value", code: "val", category: "Student Beliefs" },
//   { construct: "Self-Regulation", code: "srt", category: "Learning Behaviors" },
//   { construct: "Intrinsic Motivation", code: "amot", category: "Student Beliefs" },
//   { construct: "Extrinsic Motivation", code: "cmot", category: "Student Beliefs" },
//   { construct: "Academic Burden", code: "bur", category: "Student Beliefs" },
//   { construct: "Belongingness", code: "bel", category: "Classroom Context" },
//   { construct: "Cultural Congruity", code: "cult", category: "Classroom Context" },
//   { construct: "Withdrawal Intention", code: "with", category: "Learning Behaviors" },
// ];

// const KNOWN_CATEGORIES = ["Student Beliefs", "Learning Behaviors", "Classroom Context"];
const KNOWN_CATEGORIES = ["Student Learning"];

function AccessibleLegend({ getChart, datasets }) {
  const [, force] = useState(0);

  const toggle = (idx) => {
    const chart = getChart?.();
    if (!chart) return;
    const visible = chart.isDatasetVisible(idx);
    chart.setDatasetVisibility(idx, !visible); // ✅ reliable API
    chart.update();
    force((n) => n + 1); // re-render so aria-pressed updates
  };

  return (
    <div className="chart-legend" role="group" aria-label="Toggle data series">
      {datasets.map((ds, i) => {
        const pressed = getChart?.()?.isDatasetVisible(i) ?? true;
        return (
          <button
            key={ds.label}
            onClick={() => toggle(i)}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                toggle(i);
              }
            }}
            aria-pressed={pressed}
            className="legend-btn"
          >
            <span
              className="legend-color"
              style={{ backgroundColor: ds.borderColor }}
              aria-hidden="true"
            />
            {ds.label}
          </button>
        );
      })}
    </div>
  );
}

const TrendChart = ({ trendData }) => {
  console.log("TrendChart render", { trendData });
  const ConstructMeta = {};
  Construct_code_category.forEach(({ code, construct, category }) => {
    ConstructMeta[code] = { construct, category };
  });

  const chartRefs = useRef({});

  const makeChartRefSetter = (category) => (el) => {
    if (el) chartRefs.current[category] = el;
  };

  // ========== NEW API SHAPE: Array of rows (quizTitle/groupCode/avgScore) ==========
  if (Array.isArray(trendData)) {
    // const sorted = [...trendData].sort((a, b) => (a.quizId ?? 0) - (b.quizId ?? 0));
    const sorted = [...trendData].sort((a, b) => {
      const numA = parseInt((a.quizTitle || '').match(/\d+/)?.[0] ?? 0);
      const numB = parseInt((b.quizTitle || '').match(/\d+/)?.[0] ?? 0);
      return numA - numB;
    });

    // Base labels (x-axis) in order
    const baseLabels = Array.from(
      new Map(
        sorted.map((r) => [
          r.quizTitle || `Quiz ${r.quizId}`,
          r.quizTitle || `Quiz ${r.quizId}`,
        ])
      ).values()
    );

    // category -> construct -> { quizTitle: avgScore }
    const seriesByCategory = Object.fromEntries(
      KNOWN_CATEGORIES.map((c) => [c, {}])
    );

    for (const row of sorted) {
      const meta = ConstructMeta[row.groupCode];
      if (!meta || !KNOWN_CATEGORIES.includes(meta.category)) continue; // skip unknowns

      const label = meta.construct;
      const quizTitle = row.quizTitle || `Quiz ${row.quizId}`;
      const y = Number(row.avgScore);

      if (!seriesByCategory[meta.category][label]) {
        seriesByCategory[meta.category][label] = {};
      }
      seriesByCategory[meta.category][label][quizTitle] =
        Number.isFinite(y) ? y : null;
    }

    return (
      <div className="trend-container">
        {KNOWN_CATEGORIES.map((category) => {
          const constructs = seriesByCategory[category];
          const single = baseLabels.length === 1;

          const labels = single ? ["", baseLabels[0], ""] : baseLabels;

          const datasets = Object.entries(constructs).map(([label, series], i) => {
            const baseData = baseLabels.map((qt) =>
              qt in series ? series[qt] : null
            );
            const data = single ? [null, baseData[0] ?? null, null] : baseData;
            const { pointStyle, borderDash } = styleFor(i);

            return {
              label,
              data,
              fill: false,
              tension: 0.3,
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBorderWidth: 1.5,
              pointStyle,
              borderDash,
              borderColor: `hsl(${(i * 60) % 360}, 70%, 40%)`,
              spanGaps: true,
            };
          });

          const chartData = { labels, datasets };

          return (
            <div key={category} style={{ marginBottom: "3rem" }}>
              <h3>{category}</h3>
              <Line
                ref={makeChartRefSetter(category)}
                data={chartData}
                options={{
                  responsive: true,
                  scales: {
                    x: {
                      type: "category",
                      title: { display: true, text: "Quiz" },
                    },
                    y: {
                      min: 1,
                      max: 6,
                      title: { display: true, text: "Average Score" },
                      ticks: {
                        stepSize: 1,
                        callback: (value) =>
                          ({
                            1: "Strongly Disagree",
                            2: "Disagree",
                            3: "Slightly Disagree",
                            4: "Slightly Agree",
                            5: "Agree",
                            6: "Strongly Agree",
                          }[value] || value),
                      },
                    },
                  },
                  plugins: {
                    // legend: { position: "right" },
                    legend: { display: false }, // use DOM legend for accessibility
                    tooltip: {
                      callbacks: {
                        title: (items) =>
                          items?.[0]?.label ? `Quiz: ${items[0].label}` : "",
                      },
                    },
                  },
                }}
                aria-hidden="true" // already have non-text alternative for this
              />
              <AccessibleLegend
                getChart={() => chartRefs.current[category]}
                datasets={datasets}
              />
            </div>
          );
        })}
      </div>
    );
  }
};

export default TrendChart;
